import React from 'react';
import { CurrencyDollarIcon, CalendarIcon, ArrowTopRightOnSquareIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

/**
 * InvoiceCard - Visual invoice card with status and actions
 */
const InvoiceCard = ({ invoice, onPay, onDownload }) => {
    const formatAmount = (currency, amount) => {
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency || 'USD'
            }).format(Number(amount || 0));
        } catch {
            return `${currency || 'USD'} ${Number(amount || 0).toFixed(2)}`;
        }
    };

    const getStatusStyle = (status) => {
        const normalized = (status || '').toLowerCase();
        if (normalized === 'paid') {
            return {
                bg: 'bg-emerald-50',
                border: 'border-emerald-200',
                text: 'text-emerald-700',
                badge: 'bg-emerald-100 text-emerald-700'
            };
        }
        if (normalized === 'overdue') {
            return {
                bg: 'bg-rose-50',
                border: 'border-rose-200',
                text: 'text-rose-700',
                badge: 'bg-rose-100 text-rose-700'
            };
        }
        if (normalized === 'pending' || normalized === 'due') {
            return {
                bg: 'bg-amber-50',
                border: 'border-amber-200',
                text: 'text-amber-700',
                badge: 'bg-amber-100 text-amber-700'
            };
        }
        return {
            bg: 'bg-slate-50',
            border: 'border-slate-200',
            text: 'text-slate-700',
            badge: 'bg-slate-100 text-slate-700'
        };
    };

    const getDueInfo = (dueDate) => {
        if (!dueDate) return null;
        const date = new Date(dueDate);
        const now = new Date();
        const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { text: `${Math.abs(diffDays)}d overdue`, urgent: true };
        }
        if (diffDays === 0) {
            return { text: 'Due today', urgent: true };
        }
        if (diffDays <= 3) {
            return { text: `Due in ${diffDays}d`, urgent: true };
        }
        return { text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), urgent: false };
    };

    const style = getStatusStyle(invoice.status);
    const dueInfo = getDueInfo(invoice.due_date);
    const isPaid = invoice.status?.toLowerCase() === 'paid';

    return (
        <div className={`${style.bg} border ${style.border} rounded-2xl p-5 transition-all duration-300 hover:shadow-lg`}>
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Invoice</p>
                    <p className="text-lg font-bold text-slate-900 mt-0.5">
                        #{invoice.invoice_number || invoice.id?.slice(0, 8)}
                    </p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${style.badge}`}>
                    {invoice.status || 'Pending'}
                </span>
            </div>

            {/* Amount */}
            <div className="mb-4">
                <p className="text-3xl font-bold text-slate-900">
                    {formatAmount(invoice.currency, invoice.amount_due || invoice.amount)}
                </p>
                {invoice.description && (
                    <p className="text-sm text-slate-500 mt-1">{invoice.description}</p>
                )}
            </div>

            {/* Due date */}
            {dueInfo && !isPaid && (
                <div className={`
                    flex items-center gap-2 mb-4 text-sm
                    ${dueInfo.urgent ? 'text-rose-600' : 'text-slate-500'}
                `}>
                    <CalendarIcon className="h-4 w-4" />
                    <span>{dueInfo.text}</span>
                </div>
            )}

            {/* Paid date */}
            {isPaid && invoice.paid_at && (
                <div className="flex items-center gap-2 mb-4 text-sm text-emerald-600">
                    <CalendarIcon className="h-4 w-4" />
                    <span>Paid on {new Date(invoice.paid_at).toLocaleDateString()}</span>
                </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
                {!isPaid && onPay && (
                    <button
                        onClick={() => onPay(invoice)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-medium text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                    >
                        <CurrencyDollarIcon className="h-4 w-4" />
                        Pay Now
                    </button>
                )}
                {onDownload && (
                    <button
                        onClick={() => onDownload(invoice)}
                        className={`
                            flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors
                            ${isPaid
                                ? 'flex-1 bg-slate-100 text-slate-700 hover:bg-slate-200'
                                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}
                        `}
                    >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        {isPaid ? 'Receipt' : 'Invoice'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default InvoiceCard;
